import React, { useEffect, useMemo, useState } from "react";

import {
  getLocalStorageFieldFilterOptions,
  setLocalStorageFieldFilterOptions,
  useGetFarms,
  useGetFieldPracticeFilterOptions,
  useGetPracticeCrops,
  useGetPracticeFilteredFieldIds,
  useTranslation,
} from "utils";

import { FieldListField, ItemsWithMissingDataByFieldId } from "models";

import { CropFilterChip, FilterChip, FilterChipOption, Flex } from "components";

import { DATA_STATUS } from "pages/app/all-field-view/components/FieldFilterOptions";
import { hasItemsWithMissingData } from "pages/app/all-field-view/utils/AllFieldsUtils";

export const FieldFilterOptionsChips = ({
  fields,
  filteredFieldIds,
  setFilteredFieldIds,
  setSelectedFilterOptionsCount,
  appliedCropIds,
  appliedYears,
  appliedDataStatus,
  setAppliedCropIds,
  setAppliedYears,
  setAppliedDataStatus,
  farmId,
  handleClearFilters,
  itemsWithMissingDataByFieldId,
}: {
  fields: FieldListField[];
  filteredFieldIds: string[] | null;
  setFilteredFieldIds: (fieldIds: string[] | null) => void;
  setSelectedFilterOptionsCount: (count: number) => void;
  appliedCropIds: string[];
  appliedYears: number[];
  appliedDataStatus: DATA_STATUS;
  setAppliedCropIds: (cropIds: string[]) => void;
  setAppliedYears: (years: number[]) => void;
  setAppliedDataStatus: (dataStatus: DATA_STATUS) => void;
  farmId: string;
  handleClearFilters: () => void;
  itemsWithMissingDataByFieldId: ItemsWithMissingDataByFieldId;
}) => {
  const { t } = useTranslation("allFieldView");

  const { currentFarm } = useGetFarms();
  const { data: practiceCrops } = useGetPracticeCrops({});
  const {
    data: fieldPracticeFilterOptions,
    isFetching: isFetchingFieldPracticeFilterOptions,
    isPending: isPendingFieldPracticeFilterOptions,
  } = useGetFieldPracticeFilterOptions({ farmId: currentFarm?.id });
  const { mutateAsync: getFilteredFieldIds } = useGetPracticeFilteredFieldIds();

  const [isLoading, setIsLoading] = useState(false);
  const [activePopover, setActivePopover] = useState<"years" | "status" | null>(null);

  const mainCropOptions = useMemo(
    () => (practiceCrops ?? []).filter((crop) => crop.isMainCrop && fieldPracticeFilterOptions?.cropIds.includes(crop.id)),
    [practiceCrops, fieldPracticeFilterOptions]
  );
  const coverCropOptions = useMemo(
    () => (practiceCrops ?? []).filter((crop) => !crop.isMainCrop && !crop.isUndersowing && fieldPracticeFilterOptions?.cropIds.includes(crop.id)),
    [practiceCrops, fieldPracticeFilterOptions]
  );

  const getSelectedFilterOptionsCount = (cropIds: string[], years: number[], dataStatus: DATA_STATUS) =>
    cropIds.length + years.length + (dataStatus === DATA_STATUS.ALL ? 0 : 1);

  const handleApplyFilters = async (cropIds: string[], years: number[], dataStatus: DATA_STATUS) => {
    if (isLoading) return;

    setIsLoading(true);

    let output: string[] | null = [];
    const selectedOptionsCount = getSelectedFilterOptionsCount(cropIds, years, dataStatus);

    try {
      if (cropIds.length === 0 && years.length === 0 && dataStatus === DATA_STATUS.ALL) {
        output = null;
      } else {
        const filteredFieldIdsByPractice = await getFilteredFieldIds({ cropIds, years });

        if (dataStatus === DATA_STATUS.ALL) {
          output = filteredFieldIdsByPractice.fieldIds;
        } else {
          output = filteredFieldIdsByPractice.fieldIds.filter((fieldId) => {
            const itemsWithMissingData = itemsWithMissingDataByFieldId[fieldId];
            if (!itemsWithMissingData) return false;
            const fieldHasMissingData = hasItemsWithMissingData(itemsWithMissingData);
            return dataStatus === DATA_STATUS.COMPLETED ? !fieldHasMissingData : fieldHasMissingData;
          });
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setAppliedCropIds(cropIds);
      setAppliedYears(years);
      setAppliedDataStatus(dataStatus);
      setSelectedFilterOptionsCount(selectedOptionsCount);
      setFilteredFieldIds(output);
      setLocalStorageFieldFilterOptions({ farmId, cropIds, years, dataStatus });
      setIsLoading(false);
    }
  };

  const handleYearToggle = (year: number) => {
    const newYears = appliedYears.includes(year) ? appliedYears.filter((y) => y !== year) : [...appliedYears, year];
    handleApplyFilters(appliedCropIds, newYears, appliedDataStatus);
  };

  const handleApplyCropFilters = (cropIds: string[]) => {
    handleApplyFilters(cropIds, appliedYears, appliedDataStatus);
  };

  const handleStatusChange = (status: DATA_STATUS) => {
    handleApplyFilters(appliedCropIds, appliedYears, status);
  };

  // load saved filter options when farm changes or component mounts
  useEffect(() => {
    if (!currentFarm?.id || isPendingFieldPracticeFilterOptions || isFetchingFieldPracticeFilterOptions || isLoading) return;

    const savedFilterOptions = getLocalStorageFieldFilterOptions();

    if (!savedFilterOptions) return;
    if (savedFilterOptions.farmId !== currentFarm.id) {
      handleClearFilters();
      return;
    }

    const { cropIds, years, dataStatus } = savedFilterOptions;
    const validCropIds = cropIds.filter((cropId) => fieldPracticeFilterOptions?.cropIds.some((id) => id === cropId));
    const validYears = years.filter((year) => fieldPracticeFilterOptions?.years.some((y) => y === year));
    const validDataStatus = Object.values(DATA_STATUS).includes(dataStatus) ? dataStatus : DATA_STATUS.ALL;

    const filterCount = getSelectedFilterOptionsCount(validCropIds, validYears, validDataStatus);
    setSelectedFilterOptionsCount(filterCount);

    if (
      filteredFieldIds === null &&
      (savedFilterOptions.cropIds.length > 0 || savedFilterOptions.years.length > 0 || savedFilterOptions.dataStatus !== DATA_STATUS.ALL)
    ) {
      handleApplyFilters(savedFilterOptions.cropIds, savedFilterOptions.years, savedFilterOptions.dataStatus as DATA_STATUS);
    }
  }, [currentFarm?.id, filteredFieldIds, fieldPracticeFilterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // validate and clean up applied filters when fieldPracticeFilterOptions changes
  useEffect(() => {
    if (!fieldPracticeFilterOptions || isLoading || isPendingFieldPracticeFilterOptions || isFetchingFieldPracticeFilterOptions) return;

    const validAppliedCropIds = appliedCropIds.filter((cropId) => fieldPracticeFilterOptions.cropIds.includes(cropId));
    const validAppliedYears = appliedYears.filter((year) => fieldPracticeFilterOptions.years.includes(year));

    if (validAppliedCropIds.length !== appliedCropIds.length || validAppliedYears.length !== appliedYears.length) {
      const filterCount = getSelectedFilterOptionsCount(validAppliedCropIds, validAppliedYears, appliedDataStatus);

      setAppliedCropIds(validAppliedCropIds);
      setAppliedYears(validAppliedYears);
      setSelectedFilterOptionsCount(filterCount);
      setLocalStorageFieldFilterOptions({ farmId, cropIds: validAppliedCropIds, years: validAppliedYears, dataStatus: appliedDataStatus });
      handleApplyFilters(validAppliedCropIds, validAppliedYears, appliedDataStatus);
    }
  }, [fieldPracticeFilterOptions, appliedCropIds, appliedYears, appliedDataStatus, farmId, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDisabled = fields.length === 0;

  return (
    <Flex className="field-filter-options-chips-wrapper" align="center" gap="0.5rem" data-testid="field-filter-options-wrapper">
      {fieldPracticeFilterOptions?.years && fieldPracticeFilterOptions.years.length > 0 && (
        <FilterChip
          label={t("fieldFilterOptions.modal.sections.harvestYear.title")}
          count={appliedYears.length}
          isOpen={activePopover === "years"}
          isDisabled={isDisabled}
          onOpenChange={(open) => setActivePopover(open ? "years" : null)}
          content={
            <Flex vertical className="filter-chip-popover-content" data-testid="year-checkboxes-container">
              <FilterChipOption
                label={t("fieldFilterOptions.modal.sections.harvestYear.all")}
                selected={appliedYears.length === 0}
                onClick={() => handleApplyFilters(appliedCropIds, [], appliedDataStatus)}
              />
              {fieldPracticeFilterOptions.years.map((year) => (
                <FilterChipOption
                  key={year}
                  label={year.toString()}
                  selected={appliedYears.includes(year)}
                  onClick={() => handleYearToggle(year)}
                  dataTestId={`year-checkbox-${year}`}
                />
              ))}
            </Flex>
          }
        />
      )}

      {fieldPracticeFilterOptions?.cropIds && fieldPracticeFilterOptions.cropIds.length > 0 && (
        <CropFilterChip
          mainCropOptions={mainCropOptions}
          coverCropOptions={coverCropOptions}
          appliedCropIds={appliedCropIds}
          onApply={handleApplyCropFilters}
          isDisabled={isDisabled}
        />
      )}

      <FilterChip
        label={
          appliedDataStatus !== DATA_STATUS.ALL
            ? t(`fieldFilterOptions.modal.sections.dataStatus.options.${appliedDataStatus}`)
            : t("fieldFilterOptions.modal.sections.dataStatus.title")
        }
        count={0}
        isOpen={activePopover === "status"}
        isDisabled={isDisabled}
        onOpenChange={(open) => setActivePopover(open ? "status" : null)}
        content={
          <Flex vertical className="filter-chip-popover-content" data-testid="data-status-checkboxes-container">
            {Object.values(DATA_STATUS).map((status) => (
              <FilterChipOption
                key={status}
                label={t(`fieldFilterOptions.modal.sections.dataStatus.options.${status}`)}
                selected={appliedDataStatus === status}
                onClick={() => handleStatusChange(status)}
                dataTestId={`data-status-checkbox-${status}`}
              />
            ))}
          </Flex>
        }
      />
    </Flex>
  );
};
